import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { SkeletonCards } from '../components/ui/Skeleton';
import ErrorState from '../components/ui/ErrorState';
import { generateResumeDocx, type ResumeSections, type ProfileInfo } from '../utils/resumeDocx';
import { downloadResumePdf } from '../utils/resumePdf';
import {
  BarChart3,
  Sparkles,
  FileText,
  Briefcase,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Target,
  AlertTriangle,
  CheckCircle,
  Clock,
  X,
  Wand2,
  Copy,
  Download,
  FileDown
} from 'lucide-react';

interface Resume {
  id: number;
  original_filename: string;
  keywords: string | null;
}

interface Job {
  id: number;
  title: string;
  company_name: string;
  location: string;
  experience_level: string;
}

interface AnalysisResult {
  id: number;
  job_id: number;
  resume_id: number;
  match_score: number;
  keyword_matches: string;
  missing_keywords: string;
  suggestions: string;
  job_title: string;
  company_name: string;
  resume_name: string;
  created_at: string;
}

interface KeywordAnalysis {
  technical_skills?: string[];
  soft_skills?: string[];
  certifications?: string[];
  industry_terms?: string[];
  experience_level?: string;
  priority_keywords?: string[];
  raw?: string;
}

const Analysis: React.FC = () => {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [history, setHistory] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Keyword extraction state
  const [kwJobDesc, setKwJobDesc] = useState('');
  const [kwJobTitle, setKwJobTitle] = useState('');
  const [kwLoading, setKwLoading] = useState(false);
  const [kwResult, setKwResult] = useState<KeywordAnalysis | null>(null);

  // Match analysis state
  const [matchResumeId, setMatchResumeId] = useState<number | ''>('');
  const [matchJobId, setMatchJobId] = useState<number | ''>('');
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchResult, setMatchResult] = useState<Record<string, unknown> | null>(null);

  // Optimization state
  const [optResumeId, setOptResumeId] = useState<number | ''>('');
  const [optJobId, setOptJobId] = useState<number | ''>('');
  const [optFocus, setOptFocus] = useState('');
  const [optLoading, setOptLoading] = useState(false);
  const [optResult, setOptResult] = useState<{
    sections: ResumeSections | null;
    raw_text: string;
    job_title: string;
    company: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [profileInfo, setProfileInfo] = useState<ProfileInfo>({});
  const { user } = useAuth();
  const { toast } = useToast();

  // History expand
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [resumeData, jobData, historyData] = await Promise.all([
        api.get<{ resumes?: Resume[] }>('/api/resumes', { retry: 2 }),
        api.get<{ data?: Job[] }>('/api/jobs?limit=50&sort_by=scraped_at&sort_order=DESC', { retry: 2 }),
        api.get<AnalysisResult[]>('/api/analysis/history', { retry: 2 }),
      ]);
      setResumes(resumeData.resumes || []);
      setJobs(jobData.data || []);
      setHistory(Array.isArray(historyData) ? historyData : []);

      // Profile is only used for resume downloads — never fail the page on it.
      try {
        const pData = await api.get<{
          full_name?: string;
          email?: string;
          phone?: string;
          location?: string;
          linkedin_url?: string;
        }>('/api/profile');
        setProfileInfo({
          full_name: pData.full_name || user?.fullName || '',
          email: pData.email || user?.email || '',
          phone: pData.phone || '',
          location: pData.location || '',
          linkedin_url: pData.linkedin_url || '',
        });
      } catch {
        /* non-critical */
      }
    } catch (e) {
      setLoadError(errorText(e, 'Failed to load analysis data'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleKeywordExtract = async () => {
    if (!kwJobDesc.trim()) return;
    setKwLoading(true);
    setKwResult(null);
    try {
      const data = await api.post<{ analysis?: KeywordAnalysis }>('/api/analysis/keywords', {
        job_description: kwJobDesc,
        job_title: kwJobTitle || undefined,
      });
      setKwResult(data.analysis || null);
    } catch (e) {
      toast.error(errorText(e, 'Keyword extraction failed'));
    } finally {
      setKwLoading(false);
    }
  };

  const handleMatch = async () => {
    if (!matchResumeId || !matchJobId) return;
    setMatchLoading(true);
    setMatchResult(null);
    try {
      const data = await api.post<{ analysis?: Record<string, unknown> }>('/api/analysis/match', {
        resume_id: matchResumeId,
        job_id: matchJobId,
      });
      setMatchResult(data.analysis || null);
      await fetchData(); // refresh history
    } catch (e) {
      toast.error(errorText(e, 'Match analysis failed'));
    } finally {
      setMatchLoading(false);
    }
  };

  const handleOptimize = async () => {
    if (!optResumeId || !optJobId) return;
    setOptLoading(true);
    setOptResult(null);
    try {
      const data = await api.post<{
        sections?: ResumeSections | null;
        raw_text?: string;
        job_title?: string;
        company?: string;
      }>('/api/analysis/optimize-resume', {
        resume_id: optResumeId,
        job_id: optJobId,
        optimization_focus: optFocus || undefined,
      });
      setOptResult({
        sections: data.sections || null,
        raw_text: data.raw_text || '',
        job_title: data.job_title || '',
        company: data.company || '',
      });
    } catch (e) {
      toast.error(errorText(e, 'Resume optimization failed'));
    } finally {
      setOptLoading(false);
    }
  };

  const handleDownloadDocx = async () => {
    if (!optResult?.sections) return;
    await generateResumeDocx(optResult.sections, profileInfo, optResult.job_title, optResult.company);
  };

  const handleDownloadPdf = () => {
    if (!optResult?.sections) return;
    downloadResumePdf(optResult.sections, profileInfo, optResult.job_title, optResult.company);
  };

  // Convert sections to plain text for copy
  const getSectionsAsText = (sections: ResumeSections): string => {
    const lines: string[] = [];
    if (sections.professional_summary) {
      lines.push('PROFESSIONAL SUMMARY', sections.professional_summary, '');
    }
    if (sections.core_competencies?.length) {
      lines.push('CORE COMPETENCIES', sections.core_competencies.join(' | '), '');
    }
    if (sections.experience?.length) {
      lines.push('PROFESSIONAL EXPERIENCE');
      for (const exp of sections.experience) {
        lines.push(`${exp.title} — ${exp.company} (${exp.dates})`);
        for (const a of exp.achievements) lines.push(`  - ${a}`);
        lines.push('');
      }
    }
    if (sections.education?.length) {
      lines.push('EDUCATION');
      for (const edu of sections.education) {
        lines.push(`${edu.degree} — ${edu.institution} (${edu.year})`);
      }
      lines.push('');
    }
    if (sections.certifications?.length) {
      lines.push('CERTIFICATIONS', ...sections.certifications, '');
    }
    if (sections.technical_skills) {
      lines.push('TECHNICAL SKILLS');
      for (const [cat, skills] of Object.entries(sections.technical_skills)) {
        lines.push(`${cat}: ${skills.join(', ')}`);
      }
    }
    return lines.join('\n');
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
  };

  const parseJsonArray = (val: string | null): string[] => {
    if (!val) return [];
    try { return JSON.parse(val); } catch { return []; }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <BarChart3 className="w-8 h-8 text-primary-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analysis</h1>
          <p className="text-gray-600 dark:text-slate-400 mt-1">AI-powered keyword extraction and resume-job matching</p>
        </div>
      </div>

      {loading ? (
        <SkeletonCards count={2} className="grid-cols-1 lg:grid-cols-2" />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={fetchData} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Keyword Extraction Panel */}
          <motion.div
            className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="p-6 border-b border-gray-200 dark:border-slate-800">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Keyword Extraction</h3>
              </div>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Paste a job description to extract key skills and requirements</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Job Title (optional)</label>
                <input
                  type="text"
                  value={kwJobTitle}
                  onChange={(e) => setKwJobTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                  placeholder="e.g. Senior Software Engineer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Job Description *</label>
                <textarea
                  value={kwJobDesc}
                  onChange={(e) => setKwJobDesc(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none dark:bg-slate-800 dark:text-white"
                  placeholder="Paste the full job description here..."
                />
              </div>
              <button
                onClick={handleKeywordExtract}
                disabled={kwLoading || !kwJobDesc.trim()}
                className="w-full flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {kwLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Extract Keywords</>
                )}
              </button>

              {kwResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3 pt-4 border-t dark:border-slate-800"
                >
                  {kwResult.raw ? (
                    <pre className="text-xs bg-gray-50 dark:bg-slate-800/50 p-3 rounded-lg overflow-auto whitespace-pre-wrap dark:text-slate-300">{kwResult.raw}</pre>
                  ) : (
                    <>
                      {kwResult.technical_skills && kwResult.technical_skills.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase mb-1">Technical Skills</p>
                          <div className="flex flex-wrap gap-1">
                            {kwResult.technical_skills.map((s, i) => (
                              <span key={i} className="px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {kwResult.soft_skills && kwResult.soft_skills.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase mb-1">Soft Skills</p>
                          <div className="flex flex-wrap gap-1">
                            {kwResult.soft_skills.map((s, i) => (
                              <span key={i} className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {kwResult.priority_keywords && kwResult.priority_keywords.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase mb-1">Priority Keywords</p>
                          <div className="flex flex-wrap gap-1">
                            {kwResult.priority_keywords.map((s, i) => (
                              <span key={i} className="px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full font-medium">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {kwResult.experience_level && (
                        <p className="text-sm text-gray-600 dark:text-slate-400">Level: <span className="font-medium">{kwResult.experience_level}</span></p>
                      )}
                    </>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Match Analysis Panel */}
          <motion.div
            className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="p-6 border-b border-gray-200 dark:border-slate-800">
              <div className="flex items-center space-x-2">
                <Target className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Resume-Job Match</h3>
              </div>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Score how well a resume matches a specific job posting</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  <FileText className="w-3.5 h-3.5 inline mr-1" />Resume
                </label>
                <select
                  value={matchResumeId}
                  onChange={(e) => setMatchResumeId(e.target.value ? parseInt(e.target.value, 10) : '')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:text-white"
                >
                  <option value="">Select a resume</option>
                  {resumes.map((r) => (
                    <option key={r.id} value={r.id}>{r.original_filename}</option>
                  ))}
                </select>
                {resumes.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No resumes uploaded yet. Go to Resumes page first.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  <Briefcase className="w-3.5 h-3.5 inline mr-1" />Job Posting
                </label>
                <select
                  value={matchJobId}
                  onChange={(e) => setMatchJobId(e.target.value ? parseInt(e.target.value, 10) : '')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:text-white"
                >
                  <option value="">Select a job</option>
                  {jobs.map((j) => (
                    <option key={j.id} value={j.id}>{j.title} — {j.company_name}</option>
                  ))}
                </select>
                {jobs.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No jobs scraped yet. Scrape some companies first.</p>
                )}
              </div>
              <button
                onClick={handleMatch}
                disabled={matchLoading || !matchResumeId || !matchJobId}
                className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {matchLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
                ) : (
                  <><Target className="w-4 h-4 mr-2" />Analyze Match</>
                )}
              </button>

              {matchResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3 pt-4 border-t dark:border-slate-800"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Match Score</span>
                    <span className={`px-3 py-1 text-lg font-bold rounded-full ${scoreColor(Number(matchResult.match_score) || 0)}`}>
                      {String(matchResult.match_score ?? 0)}%
                    </span>
                  </div>
                  {matchResult.assessment != null && (
                    <p className="text-sm text-gray-600 dark:text-slate-400">{String(matchResult.assessment)}</p>
                  )}
                  {Array.isArray(matchResult.strengths) && matchResult.strengths.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase mb-1">Strengths</p>
                      {(matchResult.strengths as string[]).map((s, i) => (
                        <div key={i} className="flex items-start space-x-2 text-sm text-green-700 dark:text-green-400">
                          <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {Array.isArray(matchResult.missing_skills) && matchResult.missing_skills.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase mb-1">Missing Skills</p>
                      {(matchResult.missing_skills as string[]).map((s, i) => (
                        <div key={i} className="flex items-start space-x-2 text-sm text-amber-700 dark:text-amber-400">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => setMatchResult(null)}
                    className="text-xs text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
                  >
                    <X className="w-3 h-3 inline mr-1" />Clear result
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Resume Optimization Panel */}
      <motion.div
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <div className="p-6 border-b border-gray-200 dark:border-slate-800">
          <div className="flex items-center space-x-2">
            <Wand2 className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Resume Optimization</h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">AI-tailored resume rewrite optimized for a specific job posting</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Resume</label>
              <select
                value={optResumeId}
                onChange={(e) => setOptResumeId(e.target.value ? parseInt(e.target.value, 10) : '')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:text-white"
              >
                <option value="">Select a resume</option>
                {resumes.map((r) => (
                  <option key={r.id} value={r.id}>{r.original_filename}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Target Job</label>
              <select
                value={optJobId}
                onChange={(e) => setOptJobId(e.target.value ? parseInt(e.target.value, 10) : '')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:text-white"
              >
                <option value="">Select a job</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>{j.title} — {j.company_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Optimization Focus (optional)</label>
            <input
              type="text"
              value={optFocus}
              onChange={(e) => setOptFocus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
              placeholder="e.g. Emphasize leadership experience, ATS keywords"
            />
          </div>
          <button
            onClick={handleOptimize}
            disabled={optLoading || !optResumeId || !optJobId}
            className="flex items-center justify-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {optLoading ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Optimizing...</>
            ) : (
              <><Wand2 className="w-4 h-4 mr-2" />Optimize Resume</>
            )}
          </button>

          {optResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="pt-4 border-t dark:border-slate-800 space-y-4"
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
                  Optimized for: <span className="text-primary-600">{optResult.job_title}</span> at {optResult.company}
                </p>
                <div className="flex items-center space-x-2">
                  {optResult.sections && (
                    <>
                      <button
                        onClick={handleDownloadPdf}
                        className="flex items-center px-3 py-1.5 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30"
                      >
                        <FileDown className="w-3.5 h-3.5 mr-1" />PDF
                      </button>
                      <button
                        onClick={handleDownloadDocx}
                        className="flex items-center px-3 py-1.5 text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30"
                      >
                        <Download className="w-3.5 h-3.5 mr-1" />Word
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleCopy(
                      optResult.sections
                        ? getSectionsAsText(optResult.sections)
                        : optResult.raw_text
                    )}
                    className="flex items-center px-3 py-1.5 text-xs bg-gray-50 dark:bg-slate-800/50 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Structured Resume Preview */}
              {optResult.sections ? (
                <div className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-slate-800 rounded-xl p-6 max-h-[600px] overflow-y-auto shadow-inner" id="resume-preview">
                  {/* Professional Summary */}
                  {optResult.sections.professional_summary && (
                    <div className="mb-4">
                      <h4 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider border-b border-gray-300 dark:border-slate-700 pb-1 mb-2">Professional Summary</h4>
                      <p className="text-sm text-gray-700 dark:text-slate-300 italic">{optResult.sections.professional_summary}</p>
                    </div>
                  )}

                  {/* Core Competencies */}
                  {optResult.sections.core_competencies && optResult.sections.core_competencies.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider border-b border-gray-300 dark:border-slate-700 pb-1 mb-2">Core Competencies</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {optResult.sections.core_competencies.map((skill, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 rounded-full">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Experience */}
                  {optResult.sections.experience && optResult.sections.experience.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider border-b border-gray-300 dark:border-slate-700 pb-1 mb-2">Professional Experience</h4>
                      {optResult.sections.experience.map((exp, i) => (
                        <div key={i} className="mb-3">
                          <div className="flex items-baseline justify-between">
                            <div>
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">{exp.title}</span>
                              <span className="text-sm text-gray-600 dark:text-slate-400"> — {exp.company}</span>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-slate-400 italic ml-2 flex-shrink-0">{exp.dates}</span>
                          </div>
                          <ul className="mt-1 space-y-0.5">
                            {exp.achievements.map((a, j) => (
                              <li key={j} className="text-xs text-gray-700 dark:text-slate-300 flex items-start">
                                <span className="text-gray-400 dark:text-slate-500 mr-2 mt-0.5">-</span>
                                <span>{a}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Education */}
                  {optResult.sections.education && optResult.sections.education.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider border-b border-gray-300 dark:border-slate-700 pb-1 mb-2">Education</h4>
                      {optResult.sections.education.map((edu, i) => (
                        <div key={i} className="flex items-baseline justify-between mb-1">
                          <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{edu.degree}</span>
                            <span className="text-sm text-gray-600 dark:text-slate-400"> — {edu.institution}</span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-slate-400 ml-2">{edu.year}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Certifications */}
                  {optResult.sections.certifications && optResult.sections.certifications.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider border-b border-gray-300 dark:border-slate-700 pb-1 mb-2">Certifications</h4>
                      <ul className="space-y-0.5">
                        {optResult.sections.certifications.map((cert, i) => (
                          <li key={i} className="text-xs text-gray-700 dark:text-slate-300">- {cert}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Technical Skills */}
                  {optResult.sections.technical_skills && Object.keys(optResult.sections.technical_skills).length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider border-b border-gray-300 dark:border-slate-700 pb-1 mb-2">Technical Skills</h4>
                      {Object.entries(optResult.sections.technical_skills).map(([cat, skills]) => (
                        <div key={cat} className="mb-1">
                          <span className="text-xs font-semibold text-gray-700 dark:text-slate-300">{cat}: </span>
                          <span className="text-xs text-gray-600 dark:text-slate-400">{skills.join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Fallback: raw text */
                <pre className="text-xs text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-slate-800/50 p-4 rounded-xl overflow-auto max-h-96 whitespace-pre-wrap font-mono">
                  {optResult.raw_text}
                </pre>
              )}

              <button
                onClick={() => setOptResult(null)}
                className="text-xs text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
              >
                <X className="w-3 h-3 inline mr-1" />Clear result
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Analysis History */}
      {history.length > 0 && (
        <motion.div
          className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="p-6 border-b border-gray-200 dark:border-slate-800">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-gray-600 dark:text-slate-400" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Analysis History ({history.length})</h3>
            </div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-slate-800">
            {history.map((item) => (
              <div key={item.id} className="px-6 py-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <span className={`px-3 py-1 text-sm font-bold rounded-full ${scoreColor(item.match_score)}`}>
                      {item.match_score}%
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.job_title} at {item.company_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        Resume: {item.resume_name} &middot; {new Date(item.created_at + 'Z').toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {expandedId === item.id ? (
                    <ChevronUp className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                  )}
                </div>
                {expandedId === item.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-3 space-y-2"
                  >
                    {parseJsonArray(item.keyword_matches).length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase mb-1">Matching Skills</p>
                        <div className="flex flex-wrap gap-1">
                          {parseJsonArray(item.keyword_matches).map((s, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {parseJsonArray(item.missing_keywords).length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase mb-1">Missing Skills</p>
                        <div className="flex flex-wrap gap-1">
                          {parseJsonArray(item.missing_keywords).map((s, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {parseJsonArray(item.suggestions).length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase mb-1">Suggestions</p>
                        <ul className="text-xs text-gray-600 dark:text-slate-400 list-disc pl-4 space-y-1">
                          {parseJsonArray(item.suggestions).map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Analysis;
